import { useState } from "react";
import { mergeClasses } from "@fluentui/react-components";
import { useEaStyles } from "./sharedStyles";
import type { EaField, EaFieldPart, EaValues } from "./types";

type Props = {
  field: EaField;
  values: EaValues;
  onChange: (key: string, value: string) => void;
};

/** Einheiten-Suffix im Eingabefeld je Feldtyp. */
const unitFor = (type: EaFieldPart["type"]): string | null => {
  switch (type) {
    case "eur":
      return "EUR";
    case "percent":
      return "%";
    case "months":
      return "Monate";
    default:
      return null;
  }
};

/** Erkennt die DSI-Feldnamen in Spalte D, damit sie als Code-Chip erscheinen. */
const DSI_PATTERN = /\b([A-Z][A-Z0-9_]{2,})\b/g;

const SourceText = ({ text }: { text: string }) => {
  const styles = useEaStyles();
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  DSI_PATTERN.lastIndex = 0;
  while ((m = DSI_PATTERN.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={`${m[1]}-${m.index}`} className={styles.dsiChip}>
        {m[1]}
      </span>,
    );
    last = m.index + m[1].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

const badgeClassFor = (requirement: string, styles: ReturnType<typeof useEaStyles>) => {
  const r = requirement.toLowerCase();
  if (r.startsWith("pflicht")) return styles.reqBadgePflicht;
  if (r.startsWith("automatisch")) return styles.reqBadgeAuto;
  return undefined;
};

/** Einzelnes Eingabeelement — ohne Label, für Feld und Teilfeld gleichermaßen. */
const Control = ({
  type,
  options,
  placeholder,
  value,
  onChange,
}: {
  type: EaFieldPart["type"];
  options?: string[];
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
}) => {
  const styles = useEaStyles();
  const unit = unitFor(type);

  if (type === "yesno") {
    return (
      <div className={styles.ynRow}>
        {(["ja", "nein"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            className={mergeClasses(
              styles.ynBtn,
              value === opt && (opt === "ja" ? styles.ynJa : styles.ynNein),
            )}
            onClick={() => onChange(value === opt ? "" : opt)}
          >
            {opt === "ja" ? "Ja" : "Nein"}
          </button>
        ))}
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <textarea
        className={mergeClasses(styles.input, styles.textarea)}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (type === "select") {
    return (
      <select
        className={mergeClasses(styles.input, styles.select)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Bitte wählen…</option>
        {(options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }

  if (type === "date") {
    return (
      <input
        type="date"
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const isNumeric = type === "eur" || type === "percent" || type === "months" || type === "number";
  const input = (
    <input
      type="text"
      inputMode={isNumeric ? "decimal" : undefined}
      className={mergeClasses(styles.input, !!unit && styles.inputWithUnit)}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
  if (!unit) return input;
  return (
    <span className={styles.inputWrap}>
      {input}
      <span className={styles.unitSuffix}>{unit}</span>
    </span>
  );
};

/** Freitext-Chips („Mehrfachauswahl“ in Spalte C). */
const TagInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) => {
  const styles = useEaStyles();
  const [draft, setDraft] = useState("");
  const tags = value ? value.split("|").filter(Boolean) : [];

  const add = () => {
    const t = draft.trim();
    if (!t || tags.includes(t)) {
      setDraft("");
      return;
    }
    onChange([...tags, t].join("|"));
    setDraft("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", rowGap: 8 }}>
      {tags.length > 0 && (
        <div className={styles.tagRow}>
          {tags.map((t) => (
            <span key={t} className={styles.tag}>
              {t}
              <button
                type="button"
                aria-label={`${t} entfernen`}
                className={styles.tagRemove}
                onClick={() => onChange(tags.filter((x) => x !== t).join("|"))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className={styles.input}
        value={draft}
        placeholder={placeholder ?? "Eintrag hinzufügen und mit Enter bestätigen"}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />
    </div>
  );
};

const PartGrid = ({
  parts,
  fieldKey,
  values,
  onChange,
}: {
  parts: EaFieldPart[];
  fieldKey: string;
  values: EaValues;
  onChange: (key: string, value: string) => void;
}) => {
  const styles = useEaStyles();
  return (
    <div className={styles.partGrid}>
      {parts.map((p) => {
        const key = `${fieldKey}.${p.key}`;
        return (
          <div
            key={key}
            className={mergeClasses(styles.partField, p.span === 2 && styles.partSpan2)}
          >
            <span className={styles.partLabel}>{p.label}</span>
            <Control
              type={p.type}
              options={p.options}
              placeholder={p.placeholder}
              value={values[key] ?? ""}
              onChange={(v) => onChange(key, v)}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * Rendert ein Feld der Felddefinition inklusive seiner Metazeilen:
 * Datenquelle (Spalte D), Prüfhinweis (Spalte E) und Pflichtlogik (Spalte F).
 */
const FieldRenderer = ({ field, values, onChange }: Props) => {
  const styles = useEaStyles();
  const value = values[field.key] ?? "";

  const body = (() => {
    switch (field.type) {
      case "tags":
        return (
          <TagInput
            value={value}
            onChange={(v) => onChange(field.key, v)}
            placeholder={field.placeholder}
          />
        );
      case "document":
        return (
          <div className={styles.docBox}>
            Dokumentverknüpfung folgt mit der Dokumentenanbindung — bis dahin Fundstelle
            notieren:
            <input
              type="text"
              className={styles.input}
              style={{ maxWidth: 260 }}
              value={value}
              onChange={(e) => onChange(field.key, e.target.value)}
            />
          </div>
        );
      case "composite":
        return (
          <PartGrid
            parts={field.parts ?? []}
            fieldKey={field.key}
            values={values}
            onChange={onChange}
          />
        );
      case "coverage":
        return (
          <>
            <Control type="yesno" value={value} onChange={(v) => onChange(field.key, v)} />
            {value === "ja" && (
              <PartGrid
                parts={field.parts ?? []}
                fieldKey={field.key}
                values={values}
                onChange={onChange}
              />
            )}
          </>
        );
      case "computed":
        return <div className={styles.emptyNote}>{value || "Wird vom System ermittelt."}</div>;
      default:
        return (
          <Control
            type={field.type as EaFieldPart["type"]}
            options={field.options}
            placeholder={field.placeholder}
            value={value}
            onChange={(v) => onChange(field.key, v)}
          />
        );
    }
  })();

  return (
    <div className={mergeClasses(styles.field, field.span === 2 && styles.fieldSpan2)}>
      <div className={styles.labelRow}>
        <span className={styles.label}>{field.label}</span>
        {field.requirement && (
          <span className={mergeClasses(styles.reqBadge, badgeClassFor(field.requirement, styles))}>
            {field.requirement}
          </span>
        )}
      </div>
      {body}
      {field.hint && <span className={styles.hint}>{field.hint}</span>}
      {field.source && (
        <span className={styles.sourceRow}>
          Quelle: <SourceText text={field.source} />
        </span>
      )}
      {field.check && <span className={styles.checkRow}>{field.check}</span>}
    </div>
  );
};

export default FieldRenderer;
